import React, { useEffect, useRef, useState } from 'react';
import { InputNumber, Tooltip, Whisper } from 'rsuite';

// One employee's manual correction for the period on screen - hours by
// default, or the reimbursement adjustment in dollars, which works the same way
// (see the `valueKey` / `noteKey` / `prefix` / `postfix` props).
//
// The number is typed straight into the sheet - this is a spreadsheet row, and
// entering a handful of adjustments should not mean opening a dialog for each
// one. The reason needs room to write, so it lives in a modal that opens the
// first time an amount is entered, and afterwards hangs off a note icon.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

// How long a stepper click waits before it counts as a finished edit. Clicking
// up five times is one edit, not five - without this it would be five saves and
// five dialogs.
const STEPPER_SETTLE_MS = 600;

export default function PayrollCorrectionCell({
  employee, onClear, onRequestReason, onSaveKeepingNote, resetToken, disabled,
  valueKey = 'correction',
  noteKey = 'correctionNote',
  prefix,
  postfix = 'h',
}) {
  const stored = Number(employee[valueKey] ?? 0);
  const note = employee[noteKey];
  const [amount, setAmount] = useState(stored);

  const settleTimer = useRef(null);
  // The last value actually sent, so a blur straight after a stepper commit
  // does not send it again while the first is still in flight.
  const dispatched = useRef(null);

  // Follow the stored value rather than keeping a stale draft. Two things move
  // it: a re-pull or a save rewrites the sheet under the cell, and `resetToken`
  // changes when the reason dialog is dismissed - a typed amount that was never
  // committed has to go back to what is stored, or the cell would keep showing
  // a figure the run was not calculated on.
  useEffect(() => {
    setAmount(stored);
    dispatched.current = null;
  }, [stored, resetToken]);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const commit = (raw) => {
    window.clearTimeout(settleTimer.current);

    const next = raw === '' || raw == null ? 0 : Number(raw);
    if (!Number.isFinite(next)) { setAmount(stored); return; }
    if (next === stored || next === dispatched.current) return;

    dispatched.current = next;

    // Clearing needs no explanation - there is nothing left to explain, and the
    // note goes with it.
    if (next === 0) {
      onClear(employee, stored);
      return;
    }

    // Already explained: adjusting the figure keeps the reason it was given
    // rather than asking again on every nudge. The note icon is how a reason
    // gets reworded.
    if (note) {
      onSaveKeepingNote(employee, next);
      return;
    }

    onRequestReason(employee, next);
  };

  // Typing is an unfinished edit, so it waits for blur. A stepper click is a
  // finished one - it just settles first, so a run of clicks counts as one.
  const handleChange = (value, event) => {
    setAmount(value);
    const tag = event?.target?.tagName;
    if (!tag || tag === 'INPUT') return;
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => commit(value), STEPPER_SETTLE_MS);
  };

  return (
    <div className="payroll-correction">
      <InputNumber
        value={amount}
        onChange={handleChange}
        onBlur={() => commit(amount)}
        // Typing over a correction is the common case and the field almost
        // always holds a 0, so selecting on focus means the number can just be
        // typed rather than cleared first.
        onFocus={(event) => event.target.select()}
        onKeyDown={(event) => { if (event.key === 'Enter') event.target.blur(); }}
        disabled={disabled}
        size="sm"
        prefix={prefix}
        postfix={postfix}
        step={1}
        className="payroll-correction__input"
      />

      {note ? (
        <Whisper
          placement="top"
          trigger="hover"
          speaker={<Tooltip>{note}</Tooltip>}
        >
          <button
            type="button"
            className="payroll-correction__noteBtn"
            onClick={() => onRequestReason(employee, stored)}
            aria-label={`Reason: ${note}`}
          >
            <i className="bi bi-sticky" aria-hidden="true" />
          </button>
        </Whisper>
      ) : null}
    </div>
  );
}
